import { Skill, OnlineSkill, type SkillImportProgress, type SkillImportResult } from '../types/skill';
import { i18nService } from './i18n';
import { zip } from 'fflate';

export const MAX_LOCAL_SKILL_ARCHIVE_BYTES = 100 * 1024 * 1024;

export async function archiveSkillFolder(files: File[]): Promise<Blob> {
  if (files.length === 0 || files.length > 100_000 || files.reduce((total, file) => total + file.size, 0) > MAX_LOCAL_SKILL_ARCHIVE_BYTES) {
    throw new Error('PACKAGE_TOO_LARGE');
  }
  const entries: Record<string, Uint8Array> = {};
  for (const file of files) {
    const name = file.webkitRelativePath || file.name;
    if (name.startsWith('/') || name.split(/[\\/]/).includes('..')) throw new Error('UNSAFE_SOURCE');
    entries[name.replace(/\\/g, '/')] = new Uint8Array(await file.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    zip(entries, { level: 6 }, (error, data) => {
      if (error) reject(error);
      else resolve(new Blob([new Uint8Array(data)], { type: 'application/zip' }));
    });
  });
}

export function formatSkillImportError(result: SkillImportResult): string {
  if (result.errorCode === 'INSTALL_FAILED' && result.error) {
    return `${i18nService.t('skillImportFailed')}: ${result.error}`;
  }
  if (result.errorCode === 'NO_SKILL_FOUND') return i18nService.t('skillImportMissingManifest');
  if (result.errorCode === 'PACKAGE_TOO_LARGE') return i18nService.t('skillImportPackageTooLarge');
  if (result.errorCode === 'TOO_MANY_FILES') return i18nService.t('skillImportTooManyFiles');
  if (result.errorCode === 'UNSAFE_SOURCE') return i18nService.t('skillImportUnsafeSource');
  return i18nService.t('skillImportFailed');
}

type EmailConnectivityCheck = {
  code: 'imap_connection' | 'smtp_connection';
  level: 'pass' | 'fail';
  message: string;
  durationMs: number;
};

type EmailConnectivityTestResult = {
  testedAt: number;
  verdict: 'pass' | 'fail';
  checks: EmailConnectivityCheck[];
};

class SkillService {
  private skills: Skill[] = [];
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    await this.loadSkills();
    this.initialized = true;
  }

  async loadSkills(): Promise<Skill[]> {
    try {
      const result = await window.electron.skills.list();
      if (result.success && result.skills) {
        // 个人技能补上 source 标记，以便后端路径重写时区分
        this.skills = result.skills.map(s => ({ ...s, source: (s as any).source || 'user' }));
      } else {
        this.skills = [];
      }
      return this.skills;
    } catch (error) {
      console.error('Failed to load skills:', error);
      this.skills = [];
      return this.skills;
    }
  }

  async setSkillEnabled(id: string, enabled: boolean): Promise<Skill[]> {
    try {
      const result = await window.electron.skills.setEnabled({ id, enabled });
      if (result.success && result.skills) {
        this.skills = result.skills;
        return this.skills;
      }
      throw new Error(result.error || 'Failed to update skill');
    } catch (error) {
      console.error('Failed to update skill:', error);
      throw error;
    }
  }

  async setSkillCategory(id: string, category: string): Promise<Skill[]> {
    try {
      const result = await window.electron.skills.setCategory({ id, category });
      if (result.success && result.skills) {
        this.skills = result.skills;
        return this.skills;
      }
      throw new Error(result.error || 'Failed to set skill category');
    } catch (error) {
      console.error('Failed to set skill category:', error);
      throw error;
    }
  }

  async setSkillTags(id: string, tags: string[]): Promise<Skill[]> {
    try {
      const result = await window.electron.skills.setTags({ id, tags });
      if (result.success && result.skills) {
        this.skills = result.skills;
        return this.skills;
      }
      throw new Error(result.error || 'Failed to set skill tags');
    } catch (error) {
      console.error('Failed to set skill tags:', error);
      throw error;
    }
  }

  async deleteSkill(id: string): Promise<{ success: boolean; skills?: Skill[]; error?: string }> {
    try {
      const result = await window.electron.skills.delete(id);
      if (result.success && result.skills) {
        this.skills = result.skills;
      }
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete skill';
      console.error('Failed to delete skill:', error);
      return { success: false, error: message };
    }
  }

  async downloadSkill(source: string): Promise<{ success: boolean; skills?: Skill[]; error?: string }> {
    try {
      const result = await window.electron.skills.download(source);
      if (result.success && result.skills) {
        this.skills = result.skills;
      }
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download skill';
      console.error('Failed to download skill:', error);
      return { success: false, error: message };
    }
  }

  async importLocalSkill(
    archive: Blob,
    requestId: string,
    options: { force?: boolean; skipDuplicates?: boolean },
    onProgress: (progress: SkillImportProgress) => void,
  ): Promise<SkillImportResult> {
    let current = true;
    const invalidate = () => { current = false; };
    window.addEventListener('auth:identityChanging', invalidate);
    const unsubscribe = window.electron.skills.onImportProgress(event => {
      if (current && event.requestId === requestId) onProgress(event.progress);
    });
    try {
      const result = await window.electron.skills.uploadLocal(archive, requestId, options);
      if (!current) throw new DOMException('Account changed', 'AbortError');
      if (result.skills) this.skills = result.skills;
      return result;
    } finally {
      window.removeEventListener('auth:identityChanging', invalidate);
      unsubscribe();
    }
  }

  async getSkillsRoot(): Promise<string | null> {
    try {
      const result = await window.electron.skills.getRoot();
      if (result.success && result.path) {
        return result.path;
      }
      return null;
    } catch (error) {
      console.error('Failed to get skills root:', error);
      return null;
    }
  }

  onSkillsChanged(callback: () => void): () => void {
    return window.electron.skills.onChanged(callback);
  }

  getSkills(): Skill[] {
    return this.skills;
  }

  getEnabledSkills(): Skill[] {
    return this.skills.filter(s => s.enabled);
  }

  getSkillById(id: string): Skill | undefined {
    return this.skills.find(s => s.id === id);
  }

  async getSkillConfig(skillId: string): Promise<Record<string, string>> {
    try {
      const result = await window.electron.skills.getConfig(skillId);
      if (result.success && result.config) {
        return result.config;
      }
      return {};
    } catch (error) {
      console.error('Failed to get skill config:', error);
      return {};
    }
  }

  async setSkillConfig(skillId: string, config: Record<string, string>): Promise<boolean> {
    try {
      const result = await window.electron.skills.setConfig(skillId, config);
      return result.success;
    } catch (error) {
      console.error('Failed to set skill config:', error);
      return false;
    }
  }

  async testEmailConnectivity(
    skillId: string,
    config: Record<string, string>
  ): Promise<EmailConnectivityTestResult | null> {
    try {
      const result = await window.electron.skills.testEmailConnectivity(skillId, config);
      if (result.success && result.result) {
        return result.result;
      }
      return null;
    } catch (error) {
      console.error('Failed to test email connectivity:', error);
      return null;
    }
  }

  async fetchOnlineSkills(params: { search?: string; sort?: string; page?: number; limit?: number }): Promise<{ success: boolean; data?: { total: number; page: number; totalPages: number; skills: OnlineSkill[] }; error?: string }> {
    try {
      const lang = i18nService.getLanguage();
      return await window.electron.skills.fetchOnline({ ...params, lang });
    } catch (error) {
      console.error('Failed to fetch online skills:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch online skills' };
    }
  }

  async fetchFeaturedSkills(params: { search?: string; page?: number; limit?: number }): Promise<{ success: boolean; data?: { total: number; page: number; totalPages: number; skills: OnlineSkill[] }; error?: string }> {
    try {
      const lang = i18nService.getLanguage();
      return await window.electron.skills.fetchFeatured({ ...params, lang });
    } catch (error) {
      console.error('Failed to fetch featured skills:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch featured skills' };
    }
  }

  async installOnlineSkill(slug: string, featured?: boolean): Promise<{ success: boolean; skills?: Skill[]; error?: string }> {
    try {
      const result = await window.electron.skills.installOnline({ slug, featured });
      if (result.success && result.skills) {
        this.skills = result.skills;
      }
      return result;
    } catch (error) {
      console.error('Failed to install online skill:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to install skill' };
    }
  }

  async getAllBuiltinSkills(params?: { page?: number; pageSize?: number; search?: string; tag?: string }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const lang = i18nService.getLanguage();
      return await (window.electron.skills as any).getAllBuiltinSkills({ ...params, lang });
    } catch (error) {
      console.error('获取所有内置技能失败:', error);
      return { success: false, error: error instanceof Error ? error.message : '获取失败' };
    }
  }

  // CE 版不支持"按部门分发系统技能"，始终返回空数组；保留签名是为了让依赖此方法的 UI
  // 组件（SkillsView/SkillsPopover/BuiltinSkillsPanel）走"无分发"分支。
  async getDistributedSkills(): Promise<{ success: boolean; data?: any; error?: string }> {
    return { success: true, data: [] };
  }

  async getAutoRoutingPrompt(): Promise<string | null> {
    try {
      const result = await window.electron.skills.autoRoutingPrompt();
      return result.success ? (result.prompt || null) : null;
    } catch (error) {
      console.error('Failed to get auto-routing prompt:', error);
      return null;
    }
  }
}

export const skillService = new SkillService();
